/**
 * @fileoverview Desktop Push Notifications
 * 
 * @description
 * System-level push notifications using the browser Notifications API.
 * Only displays when the tab is not visible to avoid duplicate alerts.
 * 
 * ## Notification Flow
 * ```
 * requestPermission() → 'granted' | 'denied'
 *         ↓
 * canShowDesktopNotification() → checks visibility
 *         ↓
 * showDesktopNotification() → auto-closes after 5s
 * ```
 * 
 * @example
 * ```ts
 * import {
 *   requestNotificationPermission,
 *   showConnectionRequestNotification
 * } from '@/lib/desktopNotifications';
 * 
 * // On user action (e.g., settings toggle)
 * await requestNotificationPermission();
 * 
 * // When connection request received (tab hidden)
 * showConnectionRequestNotification('John Doe', userId);
 * ```
 * 
 * @module lib/desktopNotifications
 */

export type NotificationType = 'connection_request_received' | 'connection_request_accepted' | 'default';

/**
 * Request permission for desktop notifications
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support desktop notifications');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }

  return Notification.permission;
};

/**
 * Check if desktop notifications are supported and permitted
 */
export const canShowDesktopNotification = (): boolean => {
  return (
    'Notification' in window &&
    Notification.permission === 'granted' &&
    document.hidden // Only show when tab is not visible
  );
};

/**
 * Show a desktop notification
 */
export const showDesktopNotification = (
  title: string,
  options: {
    body?: string;
    icon?: string;
    badge?: string;
    tag?: string;
    data?: any;
    requireInteraction?: boolean;
  } = {}
): Notification | null => {
  if (!canShowDesktopNotification()) {
    return null;
  }

  try {
    const notification = new Notification(title, {
      icon: '/icon-192.png', // Default icon
      badge: '/icon-192.png',
      requireInteraction: false,
      ...options,
    });

    // Auto-close after 5 seconds unless requireInteraction is true
    if (!options.requireInteraction) {
      setTimeout(() => notification.close(), 5000);
    }

    // Handle click to focus the app
    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    return notification;
  } catch (error) {
    console.error('Error showing desktop notification:', error);
    return null;
  }
};

/**
 * Show notification for connection request received
 */
export const showConnectionRequestNotification = (userName: string, userId?: string) => {
  return showDesktopNotification('New Connection Request', {
    body: `${userName} wants to connect with you`,
    tag: 'connection_request',
    data: { type: 'connection_request', userId },
    requireInteraction: true,
  });
};

/**
 * Show notification for connection request accepted
 */
export const showConnectionAcceptedNotification = (userName: string, userId?: string) => {
  return showDesktopNotification('Connection Accepted', {
    body: `${userName} accepted your connection request`,
    tag: 'connection_accepted',
    data: { type: 'connection_accepted', userId },
  });
};

/**
 * Get the current notification permission status
 */
export const getNotificationPermission = (): NotificationPermission => {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission;
};
